import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type { PlanRequest, PlanResult } from '../types';

/**
 * Frontend client for the M2 ai-assistant backend. Follows the repo's established plain-class pattern
 * (cf. ConfigEditorClient): constructed in-component with { discoveryApi, fetchApi }, not registered
 * as an ApiRef. The single `plan()` method maps the backend's status codes to a discriminated union
 * so the widget can render disabled / invalid / error states without try/catch soup.
 */
export class AiAssistantClient {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async plan(request: PlanRequest): Promise<PlanResult> {
    const baseUrl = await this.discoveryApi.getBaseUrl('ai-assistant');

    let response: Response;
    try {
      response = await this.fetchApi.fetch(`${baseUrl}/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      });
    } catch (e: any) {
      return { status: 'error', message: e?.message ?? 'network error' };
    }

    if (response.status === 503) {
      return { status: 'disabled' };
    }

    // Parse defensively — the 500 path returns JSON, but be resilient to empty bodies.
    const data = await response.json().catch(() => ({}));

    if (response.status === 422) {
      const errors: string[] = Array.isArray(data?.details)
        ? data.details
        : [data?.error ?? 'the assistant produced an invalid plan'];
      return { status: 'invalid', errors };
    }

    if (!response.ok) {
      return {
        status: 'error',
        message: data?.message ?? data?.error ?? `request failed (${response.status})`,
      };
    }

    if (!data?.plan) {
      return { status: 'error', message: 'response did not include a plan' };
    }

    return { status: 'ok', plan: data.plan };
  }
}
